import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ProductType } from '@/features/product/types';
import { CountrySelectField } from '@/features/country/CountrySelectField';
import { countryLabel } from '@/features/agent-dispatch/countryLabel';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { CountryTab, CountryTabsList, NextCountryButton } from '@/components/CountryTabs';
import { DRUG_FINDER_HERO } from '@/features/predicate/components/drug/copy';
import {
  DRUG_FINDER_FOR,
  FINDER_QUALIFYING_LABEL,
  NOT_SURE,
  countryProgress,
  isCountryReady,
  marketKeysForCountry,
  marketsFor,
  selectedCountries,
} from '@/features/product/submission-pathways';

/** The countries with an Existing Devices finder and the copy tokens that
 *  differ between them; the qualifying-classification wording comes from
 *  FINDER_QUALIFYING_LABEL so it can't drift from the eligibility gate. */
const FINDER_SEARCH: Record<string, { article: string; noun: string }> = {
  US: { article: 'A', noun: 'predicate' },
  EU: { article: 'An', noun: 'comparator' },
};

/**
 * Product-creation Step 2: pick target countries, then a regulatory
 * classification for each. The value is a flat `{key: classification}` map
 * where a key is a country code ('US') or, for a country with sub-markets, a
 * composite key ('UK-GB' / 'UK-NI'). '' = selected but not yet classified.
 * It's persisted onto the product after creation and drives what shows in the
 * agent tables.
 *
 * The per-country tabs are the SAME control as the dossier dispatch form —
 * deliberately, so users meet it here and already know it when they generate a
 * dossier (both use `components/CountryTabs`). Single country = a flat
 * picker (no tabs). UK renders two dropdowns (Great Britain + Northern Ireland),
 * mirroring dossier's UK dispatch.
 */
export function CountryPathwayStep({
  productType,
  value,
  onChange,
  renderCountryExtra,
}: {
  productType: ProductType | null;
  value: Record<string, string>;
  onChange: (map: Record<string, string>) => void;
  /** More for one country, under its classification inside its own tab (e.g. the EU device
   *  identifiers) — shown only while that country is open. */
  renderCountryExtra?: (country: string) => ReactNode;
}) {
  const countries = useMemo(() => selectedCountries(value), [value]);
  const [activeTab, setActiveTab] = useState<string | null>(null);

  // Keep the active tab pinned to a still-selected country (mirrors dossier):
  // removing the active country auto-switches instead of stranding the user.
  useEffect(() => {
    const first = countries[0];
    if (!first) {
      if (activeTab) setActiveTab(null);
      return;
    }
    if (!activeTab || !countries.includes(activeTab)) setActiveTab(first);
  }, [countries, activeTab]);

  const handleCountries = (codes: string[]) => {
    // Expand each country to its sub-market keys (UK → UK-GB + UK-NI); keep
    // classifications for still-selected keys, newly-added ones start unset.
    const next: Record<string, string> = {};
    for (const code of codes) {
      for (const key of marketKeysForCountry(code)) next[key] = value[key] ?? '';
    }
    onChange(next);
  };

  const setPathway = (key: string, pathway: string) =>
    onChange({ ...value, [key]: pathway });

  if (!productType) {
    return (
      <p className="text-muted text-[13px]">
        Pick a category above to choose target countries and classifications.
      </p>
    );
  }

  const tabLabel = (c: string) => (c === 'UK' ? 'United Kingdom' : countryLabel(c));

  // One country renders one (or, for UK, two) classification dropdown(s).
  const renderCountryBody = (country: string) => {
    const markets = marketsFor(country, productType);
    return (
      <div className="space-y-4">
        {markets.map((m) => (
          <div key={m.key} className="space-y-1.5">
            <label
              htmlFor={`pathway-${m.key}`}
              className="block text-[13px] font-semibold text-navy"
            >
              {m.label ?? `${tabLabel(country)} — Regulatory Classification`}
            </label>
            <Select
              value={value[m.key] || undefined}
              onValueChange={(p) => setPathway(m.key, p)}
            >
              <SelectTrigger id={`pathway-${m.key}`} className="w-full max-w-xl">
                <SelectValue placeholder="Select regulatory classification…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NOT_SURE}>
                  Not sure yet — decide later
                </SelectItem>
                {m.groups.map((g) => (
                  <SelectGroup key={g.label}>
                    <SelectLabel>{g.label}</SelectLabel>
                    {g.options.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
        {/* One note per COUNTRY (UK's two sub-markets share it), reading only
            this country's dropdowns; the product-level rollup stays the
            CountryClassificationCard chip's job. */}
        {markets.some((m) => value[m.key] === NOT_SURE) && (
          <p className="max-w-xl rounded-lg border border-line bg-line/15 px-3.5 py-2.5 text-[12.5px] text-muted m-0 leading-[1.55]">
            Not sure yet? Run the{' '}
            <span className="font-semibold text-navy">Regulatory Strategy</span>{' '}
            agent — it analyzes your product and outlines the regulatory
            pathways and requirements to consider across your target markets.
            You can edit the classification anytime from the product account.
            {productType === 'device' &&
              (FINDER_SEARCH[country] ? (
                <>
                  {' '}
                  {FINDER_SEARCH[country]!.article} {country}{' '}
                  {FINDER_SEARCH[country]!.noun} search requires{' '}
                  {FINDER_QUALIFYING_LABEL[country]}, so{' '}
                  <span className="font-semibold text-navy">
                    Existing Products
                  </span>{' '}
                  can&rsquo;t run for the {country} until one is set.
                </>
              ) : (
                <>
                  {' '}
                  <span className="font-semibold text-navy">
                    Existing Products
                  </span>{' '}
                  searches cover the US and EU markets only, so a{' '}
                  {tabLabel(country)} classification doesn&rsquo;t affect them.
                </>
              ))}
            {/* The drug / biologic finders are US-only. */}
            {productType && DRUG_FINDER_FOR[productType] && country === 'US' && (
              <>
                {' '}The{' '}
                <span className="font-semibold text-navy">
                  {DRUG_FINDER_HERO[DRUG_FINDER_FOR[productType]!.kind].title}
                </span>{' '}
                needs {DRUG_FINDER_FOR[productType]!.label}, so it can&rsquo;t run until one is set.
              </>
            )}
          </p>
        )}
      </div>
    );
  };

  const isMultiCountry = countries.length > 1;
  const soleCountry = countries.length === 1 ? countries[0]! : null;

  return (
    <div className="space-y-5">
      <CountrySelectField
        id="step2-countries"
        value={countries}
        onChange={handleCountries}
        productType={productType}
        label="Target countries"
        description="Pick every country you intend to submit in — you'll choose a regulatory classification for each."
      />

      {soleCountry && (
        <>
          {renderCountryBody(soleCountry)}
          {renderCountryExtra?.(soleCountry)}
        </>
      )}

      {isMultiCountry && activeTab && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* The same control as the dossier dispatch form (shared CountryTabs). */}
          <CountryTabsList hint="Choose a classification for each country — click a country to switch.">
            {countries.map((c) => (
              <CountryTab
                key={c}
                value={c}
                label={tabLabel(c)}
                status={countryProgress(value, c)}
                testId={`pathway-tab-${c}`}
              />
            ))}
          </CountryTabsList>
          {countries.map((c, i) => {
            const next = countries[i + 1];
            return (
              <TabsContent
                key={c}
                value={c}
                data-testid={`pathway-tab-content-${c}`}
                forceMount
                className="mt-0 data-[state=inactive]:hidden"
              >
                {renderCountryBody(c)}
                {renderCountryExtra?.(c)}
                {/* Moves on once this country is classified — the same rule as the step's
                    gate, so "Not sure yet" and a GB-only UK count. */}
                {next && (
                  <NextCountryButton
                    nextLabel={tabLabel(next)}
                    problem={
                      isCountryReady(value, c)
                        ? null
                        : `Choose a classification for ${tabLabel(c)} before moving on.`
                    }
                    onNext={() => setActiveTab(next)}
                  />
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      )}

    </div>
  );
}
